import { useMemo, useState } from "react";
import { CalendarClock, TriangleAlert } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { DESIRED_PACKAGES, PACKAGE_GROUPS } from "../constants/config";

const ADMIN_FEE = 7500;
const PPN = 0.11;
const DAYS = 30;
const fmt = (n) => `Rp ${Math.round(n).toLocaleString("id-ID")}`;

const PRICE_MAP = PACKAGE_GROUPS.flatMap((g) =>
  g.items.map((i) => ({ key: `${g.name} — ${i.speed}`, price: i.price }))
);

export const ProrataCalculator = () => {
  const [selected, setSelected] = useState("");
  const [currentBill, setCurrentBill] = useState("");
  const [activeDay, setActiveDay] = useState("");

  const result = useMemo(() => {
    const pkg = PRICE_MAP.find((p) => p.key === selected);
    const oldBill = parseInt(currentBill.replace(/\D/g, ""), 10);
    const day = parseInt(activeDay, 10);
    if (!pkg || isNaN(oldBill) || oldBill <= 0 || isNaN(day) || day < 1 || day > DAYS) {
      return null;
    }
    const newTotal = (pkg.price + ADMIN_FEE) * (1 + PPN);
    const oldDays = day - 1; // hari pemakaian paket lama dalam siklus
    const newDays = DAYS - oldDays; // hari pemakaian paket baru
    const oldPart = oldBill * (oldDays / DAYS);
    const newPart = newTotal * (newDays / DAYS);
    return {
      oldDays,
      newDays,
      oldPart,
      newPart,
      newTotal,
      total: oldPart + newPart,
    };
  }, [selected, currentBill, activeDay]);

  return (
    <div
      data-testid="prorata-calculator"
      className="mt-6 bg-white rounded-2xl border border-black/10 p-7 md:p-10"
    >
      <div className="flex items-center gap-3">
        <span className="w-12 h-12 rounded-xl bg-gray-900 flex items-center justify-center shrink-0">
          <CalendarClock className="w-6 h-6 text-white" strokeWidth={2} />
        </span>
        <div>
          <h3 className="font-heading text-lg md:text-xl font-bold text-gray-900">
            Kalkulator Estimasi Tagihan Prorata Bulan Pertama
          </h3>
          <p className="text-sm text-gray-500">
            Gambaran tagihan bulan pertama setelah migrasi (masa transisi paket
            lama ke paket baru).
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-5 mt-7">
        <div className="space-y-2">
          <Label>Tagihan Bulanan Sekarang</Label>
          <Input
            data-testid="prorata-current-bill-input"
            placeholder="Contoh: 450000"
            inputMode="numeric"
            value={currentBill}
            onChange={(e) => setCurrentBill(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label>Paket Retensi Baru</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger data-testid="prorata-package-select">
              <SelectValue placeholder="Pilih paket" />
            </SelectTrigger>
            <SelectContent>
              {DESIRED_PACKAGES.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Paket Baru Aktif Tanggal Berapa</Label>
          <Input
            data-testid="prorata-day-input"
            placeholder="Contoh: 15"
            inputMode="numeric"
            min={1}
            max={30}
            value={activeDay}
            onChange={(e) => setActiveDay(e.target.value)}
          />
        </div>
      </div>

      {result && (
        <div data-testid="prorata-result" className="mt-7 border-t border-dashed border-gray-200 pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="bg-gray-50 rounded-xl p-5">
              <p className="text-xs text-gray-400 font-medium">
                Paket lama ({result.oldDays} hari)
              </p>
              <p className="font-heading font-bold text-gray-900 mt-1" data-testid="prorata-old-part">
                {fmt(result.oldPart)}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-5">
              <p className="text-xs text-gray-400 font-medium">
                Paket baru ({result.newDays} hari, sudah termasuk admin + PPN)
              </p>
              <p className="font-heading font-bold text-gray-900 mt-1" data-testid="prorata-new-part">
                {fmt(result.newPart)}
              </p>
            </div>
            <div className="bg-red-50 border border-red-100 rounded-xl p-5">
              <p className="text-xs text-[#E60000] font-medium">
                Estimasi tagihan bulan pertama
              </p>
              <p className="font-heading font-black text-xl text-[#E60000] mt-1" data-testid="prorata-total">
                {fmt(result.total)}
              </p>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-5">
            Mulai bulan kedua, tagihan full paket baru:{" "}
            <span className="font-semibold text-gray-900">{fmt(result.newTotal)}/bulan</span>{" "}
            (sudah termasuk admin Rp 7.500 + PPN 11%).
          </p>
        </div>
      )}

      <div
        data-testid="prorata-disclaimer"
        className="mt-6 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-start gap-3"
      >
        <TriangleAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800 leading-relaxed">
          <span className="font-bold">Penting:</span> hasil kalkulator ini hanya{" "}
          <span className="font-bold">estimasi/perkiraan</span> dengan asumsi 1
          siklus = 30 hari. Perhitungan resmi prorata sepenuhnya mengikuti
          sistem IndiHome dan bisa berbeda dari angka di sini. RismaCell tidak
          bertanggung jawab atas selisih perhitungan dari pihak IndiHome.
        </p>
      </div>
    </div>
  );
};
